'use client';

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiSend, FiCheckCircle, FiLoader } from 'react-icons/fi';
import { subscribeToNewsletter } from '@/app/actions/subscribe';
import { toast } from 'react-toastify';

export default function NewsletterSignup() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');
  const [email, setEmail] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    
    // Create FormData from the current form element
    const formData = new FormData(e.currentTarget);

    try {
      const result = await subscribeToNewsletter(formData);

      if (result.success) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('idle');
        // Handle specific error codes from your subscribe.ts
        const errorMsg = result.error === 'INVALID_COORDINATES' 
          ? 'Invalid Email Format' 
          : 'Uplink Failure';
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error("Newsletter submission error:", err);
      setStatus('idle');
      toast.error('CRITICAL SYSTEM ERROR');
    }
  };

  return (
    <section className="relative overflow-hidden rounded-3xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-8 md:p-12 my-20 shadow-2xl shadow-indigo-500/5">
      {/* Background Signal Grid */}
      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none" 
           style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%234f46e5' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")` }} 
      />

      <div className="relative z-10 max-w-2xl mx-auto text-center">
        <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-indigo-600 dark:text-indigo-400">
          Stay Connected
        </span>
        <h2 className="text-3xl md:text-4xl font-black tracking-tight mt-3 mb-4 text-zinc-900 dark:text-white">
          Join the Signal Loop
        </h2>
        <p className="text-zinc-600 dark:text-zinc-400 mb-8 leading-relaxed">
          Get deep-dives into Machine Learning, software architecture, and 
          technical insights delivered directly to your inbox. No noise, just signals.
        </p>

        {status === 'success' ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center py-4 text-emerald-600 dark:text-emerald-400"
          >
            <FiCheckCircle className="w-12 h-12 mb-3" />
            <span className="font-bold font-mono text-sm uppercase tracking-wider">Transmission Received! Check your inbox soon.</span>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
            <input
              name="email" // 👈 CRITICAL: Must match formData.get('email') in your action
              type="email"
              required
              placeholder="engineer@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 px-6 py-4 rounded-2xl bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all font-mono text-sm"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="px-8 py-4 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50 group"
            >
              {status === 'loading' ? (
                <FiLoader className="animate-spin" />
              ) : (
                <>
                  Subscribe <FiSend className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </>
              )}
            </button>
          </form>
        )}
        
        <p className="mt-6 text-[10px] text-zinc-500 uppercase tracking-widest font-mono">
          Secure Transmission • Privacy Guaranteed • EA-PROTO-01
        </p>
      </div>
    </section>
  );
}